import React, { useMemo } from 'react';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import TabNavigationStepper from '../../../../Components/Stepper/TabNavigationStepper/TabNavigationStepper';
import ManagerCalendarWrapper from './Calendar/ManagerCalendarWrapper';
import LaboratorySetup from '../LaboratorySetup/LaboratorySetup';
import ManagerFinance from './Finance/ManagerFinance/ManagerFinance';
import WorkplaceWorkersListWrapper from '../WorkplaceWorkers/WorkplaceWorkersList/WorkplaceWorkersListWrapper';
import CaseTypeList from '../CaseType/List/CaseTypeList';
import { useManagerContext } from '../../Services/ManagerContext';
import { HealthPlanCode } from '../../../../Enums/HealthPlanCode.enum';
import ManagerHealthPlans from './HealthPlans/ManagerHealthPlans/ManagerHealthPlans';
import ManagerBasicData from './BasicData/ManagerBasicData/ManagerBasicData';
import ManagerCertificates from './Certificates/ManagerCertificates/ManagerCertificates';
import { isAllowed } from '../../../../Services/acl.service';
import InvoicingWrapper from '../../../Finance/Components/Invoicing/InvoicingWrapper/InvoicingWrapper';
import useScope from '../../../Scope/Hooks/useScope';
import WorkplaceProviderCode from '../../../Workplace/Components/WorkplaceProviderCode/WorkplaceProviderCode';
import ManagerInsuranceCompanies from './InsuranceCompanies/ManagerInsuranceCompanies/ManagerInsuranceCompanies';
import ManagerExternalConnection from './BasicData/ManagerExternalConnection/ManagerExternalConnection';
import TreatmentConfig from './TreatmentConfig/TreatmentConfig';
import useRootWorkplaceMeta from '../../Hooks/useRootWorkplaceMeta';
import findMetaValue from '../../Helpers/findMetaValue';
import isHealthServiceProvider from '../../Helpers/isHealthServiceProvider';
import ManagerSickLeaves from './ManagerSickLeaves/ManagerSickLeaves';
import useMzcrAccess from '../../../Mzcr/Services/useMzcrAccess';
import MzcrWorkplaceSettingsWrapper from '../../../Mzcr/Components/MzcrWorkplaceSettingsWrapper';

interface Props {
	activeTab?: string;
}

const initialStepMap: Record<string, { mainTab: string; subTab?: string }> = {
	shifts: { mainTab: 'calendar', subTab: 'shifts' },
	onlineOrdering: { mainTab: 'calendar', subTab: 'onlineOrdering' },
	caseTypes: { mainTab: 'caseTypes' },
	mzcr: { mainTab: 'mzcr' },
};

const ManagerWrapper: React.FC<Props> = ({ activeTab }) => {
	const { canSetup: canSetupMzcr } = useMzcrAccess();
	const { isSuperAdmin, setHealthPlanCode, healthPlanCode, workplaceId } =
		useManagerContext();
	const { isSlovakia } = useScope();
	const { meta } = useRootWorkplaceMeta();

	const healthServiceProviderMeta = findMetaValue(
		meta,
		'healthServiceProvider',
	);

	const initialStep = activeTab
		? initialStepMap[activeTab]
		: { mainTab: 'calendar' };

	const canManageWorkplace = isAllowed('manager_workplace');
	const canManageDoctors = isAllowed('manager_doctors');
	const canManageFinance = isAllowed('manager_finance');
	const canManageInvoicing = isAllowed('manager_invoicing');
	const canManageCertificates = isAllowed('manager_certificates');
	const canManageLaboratories = isAllowed('manager_laboratories');
	const canManageInsuranceCompanies =
		isAllowed('manager_insurance_companies') &&
		isHealthServiceProvider(healthServiceProviderMeta);
	const canManageSickLeaves = isAllowed('manager_sick_leaves');

	const stepMeta = useMemo(() => {
		const temp: { name: string; key: string }[] = [];

		if (canManageWorkplace) {
			temp.push({ name: 'component.managerWrapper.calendar', key: 'calendar' });
			temp.push({
				name: 'component.managerWrapper.caseTypes',
				key: 'caseTypes',
			});
		}

		if (canManageDoctors) {
			temp.push({ name: 'component.managerWrapper.workers', key: 'workers' });
		}

		if (canManageFinance) {
			//DISABLED FOR MANAGER KEEP ONLY IN SUPER ADMIN FOR NOW
			//temp.push({ name: 'component.managerWrapper.finance', key: 'finance' });
		}

		if (canManageInvoicing) {
			temp.push({
				name: 'component.managerWrapper.invoicing',
				key: 'invoicing',
			});
		}

		if (canManageWorkplace) {
			temp.push({
				name: 'component.managerWrapper.basicData',
				key: 'basicData',
			});
		}

		if (canManageCertificates) {
			temp.push({
				name: 'component.managerWrapper.certificates',
				key: 'certificates',
			});
		}

		if (canManageLaboratories) {
			temp.push({
				name: 'component.managerWrapper.laboratories',
				key: 'laboratories',
			});
		}

		if (canManageWorkplace) {
			temp.push({
				name: 'component.managerWrapper.healthPlans',
				key: 'healthPlans',
			});
		}

		if (isSlovakia) {
			temp.push({
				name: 'component.managerWrapper.ambulanceCode',
				key: 'ambulanceCode',
			});
		}

		if (canManageInsuranceCompanies) {
			temp.push({
				name: 'component.managerWrapper.insuranceCompanies',
				key: 'insuranceCompanies',
			});
		}

		if (canManageSickLeaves) {
			temp.push({
				name: 'component.managerWrapper.sickLeaveCertificates',
				key: 'sickLeaveCertificates',
			});
		}

		if (canManageWorkplace) {
			temp.push({
				name: 'component.managerWrapper.externalConnection',
				key: 'externalConnection',
			});
		}

		if (canSetupMzcr && canManageWorkplace) {
			temp.push({
				name: 'component.managerWrapper.mzcr',
				key: 'mzcr',
			});
		}

		temp.push({
			name: 'component.managerWrapper.treatmentConfig',
			key: 'treatmentConfig',
		});

		return temp;
	}, [
		canManageWorkplace,
		canManageDoctors,
		canManageFinance,
		canManageInvoicing,
		canManageCertificates,
		canManageLaboratories,
		isSlovakia,
		canManageInsuranceCompanies,
		canManageSickLeaves,
		canSetupMzcr,
	]);

	const componentMap: Record<
		string,
		(params: NavigationStepperComponentParams) => JSX.Element
	> = {
		calendar: () => <ManagerCalendarWrapper activeTab={initialStep?.subTab} />,
		caseTypes: (params: NavigationStepperComponentParams) => (
			<CaseTypeList
				workplaceId={workplaceId}
				healthPlanCode={healthPlanCode}
				openShifts={() => params.setActiveStepPath('calendar')}
			/>
		),
		workers: () => (
			<WorkplaceWorkersListWrapper showAllWorkplaces={isSuperAdmin} />
		),
		finance: () => <ManagerFinance />,
		invoicing: () => <InvoicingWrapper isSuperAdmin={isSuperAdmin} />,
		basicData: () => <ManagerBasicData workplaceId={workplaceId} />,
		certificates: () => <ManagerCertificates />,
		laboratories: () => <LaboratorySetup isSuperAdmin={isSuperAdmin} />,
		healthPlans: (params: NavigationStepperComponentParams) => (
			<ManagerHealthPlans
				openCaseTypes={(selectedCode: HealthPlanCode) => {
					setHealthPlanCode(selectedCode);
					params.setActiveStepPath('caseTypes');
				}}
			/>
		),
		externalConnection: () => (
			<ManagerExternalConnection workplaceId={workplaceId} />
		),
		ambulanceCode: () => <WorkplaceProviderCode workplaceId={workplaceId} />,
		insuranceCompanies: () => <ManagerInsuranceCompanies />,
		sickLeaveCertificates: () => (
			<ManagerSickLeaves workplaceId={workplaceId} />
		),
		mzcr: () => <MzcrWorkplaceSettingsWrapper workplaceId={workplaceId} />,
		treatmentConfig: () => <TreatmentConfig workplaceId={workplaceId} />,
	};

	const steps: NavigationStepperItemType[] = stepMeta.map(({ name, key }) => ({
		name,
		key,
		component: componentMap[key],
	}));

	const handleOnStepChange = (key: string) => {
		if (key !== 'caseTypes') {
			setHealthPlanCode(undefined);
		}
	};

	return (
		<TabNavigationStepper
			steps={steps}
			initialStepPath={initialStep.mainTab}
			fallbackStep='calendar'
			onStepChange={handleOnStepChange}
			canSkip
		/>
	);
};

export default ManagerWrapper;
